import React, { useMemo } from 'react';
import { TrendingUp } from 'lucide-react';
import type { ForecastData } from '../../utils/analyticsCalculations';

export interface ForecastingChartProps {
  forecast: ForecastData[];
  title?: string;
  historicalData?: Array<{ date: string; value: number }>;
  loading?: boolean;
}

export const ForecastingChart: React.FC<ForecastingChartProps> = ({
  forecast,
  title = 'Revenue Forecast',
  historicalData = [],
  loading = false,
}) => {
  const formatCurrency = (value: number) => {
    return `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const chartData = useMemo(() => {
    if (forecast.length === 0 && historicalData.length === 0) return null;

    const all = [
      ...historicalData.map(d => ({
        date: d.date, value: d.value, isForecast: false,
        confidenceLower: d.value, confidenceUpper: d.value,
      })),
      ...forecast.map(d => ({
        date: d.date, value: d.value, isForecast: true,
        confidenceLower: d.confidenceLower ?? d.value,
        confidenceUpper: d.confidenceUpper ?? d.value,
      })),
    ];

    const allValues = all.flatMap(d => [d.value, d.confidenceLower, d.confidenceUpper]);
    const maxValue = Math.max(...allValues, 1);
    const minValue = Math.min(...allValues, 0);

    const padding = 40;
    const chartWidth = 800;
    const chartHeight = 300;
    const graphWidth = chartWidth - padding * 2;
    const graphHeight = chartHeight - padding * 2;

    const getX = (i: number) => padding + (i / (all.length - 1 || 1)) * graphWidth;
    const getY = (v: number) => padding + graphHeight - ((v - minValue) / (maxValue - minValue || 1)) * graphHeight;

    const historicalCount = historicalData.length;

    return { all, maxValue, minValue, padding, chartWidth, chartHeight, graphWidth, graphHeight, getX, getY, historicalCount };
  }, [forecast, historicalData]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!chartData || forecast.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">30-day forecast</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          Insufficient data for forecasting (minimum 3 days of orders required)
        </div>
      </div>
    );
  }

  const { all, maxValue, minValue, padding, chartWidth, chartHeight, graphWidth, graphHeight, getX, getY, historicalCount } = chartData;

  const forecastPoints = all.filter(d => d.isForecast);
  const forecastStartIdx = all.findIndex(d => d.isForecast);

  const confidencePath = forecastPoints.length > 0
    ? (() => {
        const indices = all.map((d, i) => d.isForecast ? i : -1).filter(i => i >= 0);
        const upperPath = indices.map(i => `${getX(i)},${getY(all[i].confidenceUpper)}`).join(' L ');
        const lowerPath = indices.reverse().map(i => `${getX(i)},${getY(all[i].confidenceLower)}`).join(' L ');
        return `M ${upperPath} L ${lowerPath} Z`;
      })()
    : '';

  const historicalLinePath = all
    .slice(0, historicalCount)
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.value)}`)
    .join(' ');

  const bridgeStart = historicalCount > 0 ? historicalCount - 1 : 0;
  const forecastLinePath = all
    .slice(bridgeStart)
    .filter((_, i) => i === 0 || all[bridgeStart + i]?.isForecast !== false)
    .map((d, i) => {
      const idx = bridgeStart + (i === 0 ? 0 : all.slice(bridgeStart).findIndex((dd, j) => j > 0 && (j === i || dd.isForecast)));
      const actualIdx = i === 0 ? bridgeStart : forecastStartIdx + (i - 1);
      return `${i === 0 ? 'M' : 'L'} ${getX(actualIdx)} ${getY(all[actualIdx].value)}`;
    })
    .join(' ');

  const forecastLinePathClean = (() => {
    const points: string[] = [];
    if (historicalCount > 0) {
      points.push(`M ${getX(historicalCount - 1)} ${getY(all[historicalCount - 1].value)}`);
    }
    for (let i = historicalCount; i < all.length; i++) {
      points.push(`${points.length === 0 ? 'M' : 'L'} ${getX(i)} ${getY(all[i].value)}`);
    }
    return points.join(' ');
  })();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
          <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">30-day forecast with 95% confidence interval</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
          {[0, 1, 2, 3, 4].map((i) => {
            const y = padding + (i / 4) * graphHeight;
            return (
              <g key={i}>
                <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" className="text-gray-200 dark:text-gray-700" />
                <text x={padding - 10} y={y + 4} textAnchor="end" className="text-xs fill-gray-500 dark:fill-gray-400">
                  {Math.round(maxValue - (i / 4) * (maxValue - minValue))}
                </text>
              </g>
            );
          })}

          {forecastStartIdx >= 0 && (
            <line
              x1={getX(forecastStartIdx)}
              y1={padding}
              x2={getX(forecastStartIdx)}
              y2={chartHeight - padding}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="6 3"
              className="text-gray-300 dark:text-gray-600"
            />
          )}

          {confidencePath && (
            <path d={confidencePath} fill="rgb(59, 130, 246)" fillOpacity="0.1" />
          )}

          {historicalLinePath && (
            <path d={historicalLinePath} stroke="rgb(34, 197, 94)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          )}

          {forecastLinePathClean && (
            <path d={forecastLinePathClean} stroke="rgb(59, 130, 246)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 3" />
          )}

          {all.map((point, index) => (
            <g key={index}>
              <circle cx={getX(index)} cy={getY(point.value)} r="3" fill={point.isForecast ? 'rgb(59, 130, 246)' : 'rgb(34, 197, 94)'} />
              <title>
                {point.date}: {formatCurrency(point.value)}
                {point.isForecast ? ` (forecast, range: ${formatCurrency(point.confidenceLower)} - ${formatCurrency(point.confidenceUpper)})` : ''}
              </title>
            </g>
          ))}

          {forecastStartIdx >= 0 && (
            <text x={getX(forecastStartIdx) + 4} y={padding + 12} className="text-xs fill-gray-400 dark:fill-gray-500">
              Forecast
            </text>
          )}
        </svg>
      </div>

      <div className="mt-4 flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-5 h-0.5 bg-green-500 rounded"></div>
          <span>Historical</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-0.5 bg-blue-500 rounded" style={{ backgroundImage: 'repeating-linear-gradient(90deg, rgb(59,130,246) 0, rgb(59,130,246) 4px, transparent 4px, transparent 7px)' }}></div>
          <span>Forecast</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-3 bg-blue-500/10 border border-blue-300/30 rounded-sm"></div>
          <span>95% Confidence</span>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Next 7 Days</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(forecast.slice(0, 7).reduce((sum, f) => sum + f.value, 0))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Next 30 Days</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(forecast.reduce((sum, f) => sum + f.value, 0))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg Daily</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {formatCurrency(
                forecast.length > 0
                  ? forecast.reduce((sum, f) => sum + f.value, 0) / forecast.length
                  : 0
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
